import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["connectButton", "fetchListsButton"]

  static values = {
    connectUrl: String,
    fetchListsUrl: String,
    connected: Boolean,
    fetched: Boolean,
    integrationProviderId: String,
    integrationId: String
  }

  connect() {
    if (this.connectedValue && this.fetchedValue && this.hasFetchListsButtonTarget) {
      // Already connected & lists fetched — skip re-verify and reload lists directly
      requestAnimationFrame(() => this.fetchLists())
    } else if (this.connectedValue) {
      // Connected but lists not yet fetched — verify to surface the Fetch Lists button
      this.secretConnect()
    }
  }

  async secretConnect(event) {
    event?.preventDefault()

    const secretId = this.getIndexValue(this.element.querySelector('input[name*="[key]"]'))
    await this.updateFrames(secretId);
    const valueInput = this.element.querySelector('input[name*="[value]"]')
    const secretValue = valueInput?.value?.trim()
    const keyInput = this.element.querySelector('input[name*="[key]"]')
    const secretKey = keyInput?.value || "apiKey"
    const icon = this.connectButtonTarget.querySelector('i')
    icon.classList.remove('ph-plugs')
    icon.classList.add('ph-circle-notch', 'ph-spin')

    const formData = new FormData()
    formData.append('value', secretValue)
    formData.append('dom_id', secretId)
    formData.append('key', secretKey)
    if (this.integrationProviderIdValue) formData.append('integration_provider_id', this.integrationProviderIdValue)
    if (this.integrationIdValue) formData.append('integration_id', this.integrationIdValue)
    // Include config fields from the form so the server can build valid URLs/templates
    this._appendConfigFields(formData)

    fetch(this.connectUrlValue, {
      method: 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => response.text())
    .then(html => {
      icon.classList.remove('ph-circle-notch', 'ph-spin')
      icon.classList.add('ph-plugs')
      Turbo.renderStreamMessage(html)
      // Wait one frame for Turbo to paint the DOM before checking for the button
      const that = this
      setTimeout(() => {
        if (that.hasFetchListsButtonTarget) that.fetchLists()
      }, 100)
    })
  }

  async fetchLists(event) {
    event?.preventDefault()

    const secretId = this.getIndexValue(this.element.querySelector('input[name*="[key]"]'))
    // Ensure frame IDs are prefixed with secretId so the turbo stream targets match —
    // this is also needed on page-reload auto-fetch where secretConnect() was never called.
    await this.updateFrames(secretId)

    const valueInput = this.element.querySelector('input[name*="[value]"]')
    const secretValue = valueInput?.value?.trim()
    const keyInput = this.element.querySelector('input[name*="[key]"]')
    const secretKey = keyInput?.value || "apiKey"
    const icon = this.fetchListsButtonTarget.querySelector('i')
    icon.classList.remove('ph-list')
    icon.classList.add('ph-circle-notch', 'ph-spin')

    const formData = new FormData()
    formData.append('value', secretValue)
    formData.append('dom_id', secretId)
    formData.append('key', secretKey)
    if (this.integrationProviderIdValue) formData.append('integration_provider_id', this.integrationProviderIdValue)
    if (this.integrationIdValue) formData.append('integration_id', this.integrationIdValue)

    fetch(this.fetchListsUrlValue, {
      method: 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => response.text())
    .then(html => {
      icon.classList.remove('ph-circle-notch', 'ph-spin')
      icon.classList.add('ph-list')
      Turbo.renderStreamMessage(html)
    })
  }

  validateSecret(event) {
    event.preventDefault()
    
    const valueInput = event.currentTarget

    if (!valueInput.value.trim()) {
      this.connectButtonTarget.disabled = true
      this.connectButtonTarget.classList.add('bg-gray-400','cursor-not-allowed')
      this.connectButtonTarget.classList.remove('bg-purple-dark')
    } else {
      this.connectButtonTarget.disabled = false
      this.connectButtonTarget.classList.remove('bg-gray-400','cursor-not-allowed')
      this.connectButtonTarget.classList.add('bg-purple-dark')
    }
  }

  getIndexValue(el) {
    const match = el.name.match(/\[integration_secrets_attributes\]\[(\d+)\]/);
    return match ? parseInt(match[1], 10) : this.element.dataset.secretId;
  }

  // Traverse up to the nearest form and collect integration[config][*] inputs so
  // the server can substitute URL/template placeholders during a connection test.
  _appendConfigFields(formData) {
    const form = this.element.closest("form")
    if (!form) return
    form.querySelectorAll("input[name^='integration[config]'], textarea[name^='integration[config]']").forEach((input) => {
      const match = input.name.match(/integration\[config\]\[(.+)\]/)
      if (match && input.value) {
        formData.append(`config[${match[1]}]`, input.value)
      }
    })
  }

  toggleExcluded(event) {
    const checkbox = event.target
    const row = checkbox.closest("tr")
    const hidden = row.querySelector(".hidden-destination-field")

    hidden.disabled = !checkbox.checked
  }

  updateFrames(secretId) {
    this.element.querySelectorAll('.turboFrames').forEach(frame => {
      if(frame.id.includes(secretId)) return;

      frame.setAttribute('id', `${secretId}_${frame.id}`);
    })
  }
}
